import { FC, useState } from 'react';

import {
  SpinnerThin,
  StatusCorrectIcon,
  StatusIncorrectIcon,
} from '@dynamika-shared/ui/icons';
import { ChevronIcon } from '@dynamika-shared/ui/icons/Chevron';

import { twJoin } from '@utils/twMerge';
import type {
  UploadReportedRow,
  UploadSummary,
  UploadSummaryLine,
} from '@restore/clinicalTrialSiteRecipients/clinicalTrialSiteRecipients.utils';

export type UploadRecipientsSummaryProps = {
  summary?: UploadSummary | null;
};

const STATUS_COPY: Record<
  UploadSummaryLine['status'],
  { label: string; description: string }
> = {
  CREATED: {
    label: 'Created',
    description: 'data rows that produced at least one new assignment',
  },
  SKIPPED: {
    label: 'Skipped',
    description: 'data rows where all specified events were already assigned',
  },
  REJECTED: {
    label: 'Rejected',
    description: 'data rows that failed validation',
  },
};

const STATUS_ORDER: UploadSummaryLine['status'][] = [
  'CREATED',
  'SKIPPED',
  'REJECTED',
];

const isFailing = (line: UploadSummaryLine) =>
  line.status === 'REJECTED' && line.count > 0;

// #B4BDD3, sampled from the Figma frame. `status-correct.svg` hard-codes its
// green fill, and a CSS `fill` rule outranks the SVG presentation attribute,
// so the circle is repainted here without touching the shared icon.
const MUTED_TICK = '[&_circle]:fill-pastelBlue';

type StatusRowProps = {
  line?: UploadSummaryLine;
  status: UploadSummaryLine['status'];
};

const StatusRow: FC<StatusRowProps> = ({ line, status }) => {
  const { label, description } = STATUS_COPY[status];
  const failing = line ? isFailing(line) : false;

  return (
    <div className='flex items-start gap-1.5 py-1.5'>
      <span className='flex h-3 w-3 shrink-0 items-center justify-center'>
        {!line && <SpinnerThin className='h-2.5 w-2.5 text-dodgerBlue' />}
        {line && !failing && (
          <StatusCorrectIcon
            className={twJoin('h-2 w-2', status === 'SKIPPED' && MUTED_TICK)}
          />
        )}
        {line && failing && <StatusIncorrectIcon className='h-2 w-2' />}
      </span>

      <div className='min-w-0 flex-1'>
        <p className='text-1424 font-medium text-black'>{label}</p>
        <p className='text-1216 text-dimGray'>{description}</p>
      </div>

      <span
        data-testid={`upload-recipients-summary-${status.toLowerCase()}`}
        className={twJoin(
          'shrink-0 rounded-sm border px-1.5 py-[0.2rem] text-1424 font-medium',
          !line && 'border-pastelBlue text-pastelBlue',
          line && !failing && 'border-pastelBlue text-black',
          failing && 'border-redOrange text-redOrange'
        )}
      >
        {line ? line.count : '–'}
      </span>
    </div>
  );
};

/**
 * A refusal is outlined in red so it reads as something to act on; a skipped
 * row gets a quiet blue fill and no outline at all, so it sits in the panel as
 * information rather than as a second alarm.
 */
const TONE_CLASSES = {
  error: {
    frame: 'border border-redOrange bg-seashell',
    bullet: 'bg-redOrange',
  },
  neutral: { frame: 'bg-blueTone', bullet: 'bg-pastelBlue' },
} as const;

type RowsListProps = {
  rows: UploadReportedRow[];
  title: string;
  tone: keyof typeof TONE_CLASSES;
  testId: string;
  fallbackReason: string;
};

const RowsList: FC<RowsListProps> = ({
  rows,
  title,
  tone,
  testId,
  fallbackReason,
}) => {
  const [open, setOpen] = useState(true);

  const { frame, bullet } = TONE_CLASSES[tone];

  return (
    <div data-testid={testId} className={twJoin('my-1 rounded-sm', frame)}>
      <button
        type='button'
        data-testid={`${testId}-toggle`}
        className='flex w-full items-center justify-between px-2 py-1.5 text-1424 font-medium text-black'
        onClick={() => setOpen(current => !current)}
      >
        {title}
        <ChevronIcon dir={open ? 'up' : 'down'} className='h-2 w-2' />
      </button>

      {open && (
        <>
          <hr className='mx-2 border-brightGray' />

          <ul className='flex flex-col gap-1.5 px-2 py-1.5'>
            {rows.map(({ rowNumber, reasons }, index) => (
              <li
                key={rowNumber ?? `unnumbered-${index}`}
                className='flex items-start gap-0.5'
              >
                <span
                  className={twJoin(
                    'mt-[0.9rem] h-[0.5rem] w-[0.5rem] shrink-0 rounded-full',
                    bullet
                  )}
                />

                <div className='min-w-0 flex-1'>
                  <p className='text-1424 font-semibold text-black'>
                    {rowNumber === undefined ? 'Row —:' : `Row ${rowNumber}:`}
                  </p>

                  <p className='text-1216 font-medium text-dimGray'>
                    {reasons.length ? reasons.join(', ') : fallbackReason}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

const ROW_LIST_CONFIG: Partial<
  Record<UploadSummaryLine['status'], Omit<RowsListProps, 'rows'>>
> = {
  SKIPPED: {
    title: 'Skipped rows',
    tone: 'neutral',
    testId: 'upload-recipients-skipped',
    fallbackReason: 'All specified events were already assigned.',
  },
  REJECTED: {
    title: 'Rejected Rows',
    tone: 'error',
    testId: 'upload-recipients-errors',
    fallbackReason: 'Failed validation.',
  },
};

export const UploadRecipientsSummary: FC<UploadRecipientsSummaryProps> = ({
  summary,
}) => {
  const lineFor = (status: UploadSummaryLine['status']) =>
    summary?.lines.find(line => line.status === status);

  const rowsFor = (
    status: UploadSummaryLine['status']
  ): UploadReportedRow[] => {
    if (status === 'SKIPPED') return summary?.skippedRows ?? [];
    if (status === 'REJECTED') return summary?.rejectedRows ?? [];

    return [];
  };

  return (
    <div data-testid='upload-recipients-summary'>
      <h6 className='text-1424 font-semibold text-black'>Validation Results</h6>

      <hr className='mt-1.5 border-brightGray' />

      {STATUS_ORDER.map(status => {
        const listConfig = ROW_LIST_CONFIG[status];
        const rows = rowsFor(status);

        return (
          <div key={status}>
            <StatusRow status={status} line={lineFor(status)} />

            {listConfig && rows.length > 0 && (
              <RowsList rows={rows} {...listConfig} />
            )}

            <hr className='border-brightGray' />
          </div>
        );
      })}
      {summary && !summary.processingComplete && (
        <p
          data-testid='upload-recipients-incomplete'
          className='mt-1.5 text-1216 text-redOrange'
        >
          Processing did not complete
          {summary.failedAtRowNumber !== undefined &&
            ` at row ${summary.failedAtRowNumber}`}
          .{summary.failureReason ? ` ${summary.failureReason}` : ''}
        </p>
      )}
    </div>
  );
};
